import type {ClickAudioConfig} from "../api/presentations.ts";

const MIN_LOOPED_PLAY_COUNT = 2;
const MAX_PLAY_COUNT = 100;

const normalizePlayCount = (config: ClickAudioConfig): number => {
    if (!config.loop) return 1;

    const requestedCount = Math.floor(Number(config.playCount));
    if (!Number.isFinite(requestedCount)) return MIN_LOOPED_PLAY_COUNT;
    return Math.max(MIN_LOOPED_PLAY_COUNT, Math.min(MAX_PLAY_COUNT, requestedCount));
};

export class ClickAudioPlayer {
    private readonly activeAudio = new Map<string, { audio: HTMLAudioElement; playbackId: number }>();
    private readonly persistentSlidePlaybackIds = new Map<string, number>();
    private nextPlaybackId = 1;

    play(key: string, config?: ClickAudioConfig | null): void {
        const url = String(config?.url ?? '').trim();
        if (!url || !config) return;

        this.stopAll();

        const audio = new Audio(url);
        const playbackId = this.nextPlaybackId++;
        const totalPlayCount = normalizePlayCount(config);
        let completedPlayCount = 0;

        const cleanup = () => {
            if (this.activeAudio.get(key)?.playbackId === playbackId) {
                this.activeAudio.delete(key);
                this.persistentSlidePlaybackIds.delete(key);
            }
            audio.onended = null;
            audio.onerror = null;
        };

        const stopFailedPlayback = (error?: unknown) => {
            if (error) {
                console.warn('Unable to play click audio.', error);
            }
            if (this.activeAudio.get(key)?.playbackId === playbackId) {
                this.stop(key);
            } else {
                audio.onended = null;
                audio.onerror = null;
                audio.pause();
                audio.removeAttribute('src');
                audio.load();
            }
        };

        const startPlayback = () => {
            try {
                const result = audio.play();
                result?.catch(stopFailedPlayback);
            } catch (error) {
                stopFailedPlayback(error);
            }
        };

        audio.preload = 'auto';
        audio.onended = () => {
            completedPlayCount += 1;
            if (completedPlayCount >= totalPlayCount) {
                cleanup();
                return;
            }

            audio.currentTime = 0;
            startPlayback();
        };
        audio.onerror = () => stopFailedPlayback(audio.error);
        this.activeAudio.set(key, { audio, playbackId });
        startPlayback();
    }

    persistAcrossSlideChanges(key: string): number | null {
        const active = this.activeAudio.get(key);
        if (!active) return null;

        this.persistentSlidePlaybackIds.set(key, active.playbackId);
        return active.playbackId;
    }

    stopForSlideChange(): void {
        [...this.activeAudio.entries()].forEach(([key, active]) => {
            if (this.persistentSlidePlaybackIds.get(key) === active.playbackId) return;
            this.stop(key);
        });
    }

    stop(key: string): void {
        const active = this.activeAudio.get(key);
        if (!active) return;

        this.persistentSlidePlaybackIds.delete(key);
        const {audio} = active;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        this.activeAudio.delete(key);
    }

    stopAll(): void {
        [...this.activeAudio.keys()].forEach((key) => this.stop(key));
    }
}
